import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { BillsByCounterpartyItem, BillStatus } from "@common/types/bill";
import { BillFilters } from "@common/contracts/bill.contract";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { Amount } from "@design-system/Amount/Amount";
import { BillsByVendorColumn } from "@common/types/views/billViews";
import classNames from "classnames";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import styles from "./DrilldownAmountCell.module.scss";

interface BillsByVendorViewCallbacks {
    onDrilldown?: (params: BillFilters) => void;
}

const getDrilldownParams = (
    item: BillsByCounterpartyItem,
    columnType?: BillsByVendorColumn,
): BillFilters => {
    const baseParams: BillFilters =
        item.counterpartyId === null
            ? { withEmptyOrUnverifiedCounterparty: true }
            : { counterpartiesIds: [item.counterpartyId] };

    // Filter by externalStatus to align with the by-counterparty aggregation,
    // which buckets non-accrual entities by the effective status (internal
    // sources resolve to `status`, integrations to `externalStatus`).
    switch (columnType) {
        case BillsByVendorColumn.OVERDUE:
            return {
                ...baseParams,
                externalBillStatuses: [BillStatus.OPEN],
                overdue: true,
            };
        case BillsByVendorColumn.DUE_1_TO_30:
            return {
                ...baseParams,
                externalBillStatuses: [BillStatus.OPEN],
                minDue: 1,
                maxDue: 30,
            };
        case BillsByVendorColumn.DUE_31_TO_60:
            return {
                ...baseParams,
                externalBillStatuses: [BillStatus.OPEN],
                minDue: 30,
                maxDue: 60,
            };
        case BillsByVendorColumn.DUE_61_PLUS:
            return {
                ...baseParams,
                externalBillStatuses: [BillStatus.OPEN],
                minDue: 60,
            };
        case BillsByVendorColumn.TOTAL_OPEN:
            return {
                ...baseParams,
                externalBillStatuses: [BillStatus.OPEN],
            };
        case BillsByVendorColumn.TOTAL_PAID:
            return {
                ...baseParams,
                externalBillStatuses: [BillStatus.PAID],
            };
        case BillsByVendorColumn.BILLS_COUNT:
        default:
            return baseParams;
    }
};

export const DrilldownAmountCell = memo(
    ({
        getValue,
        row,
        table,
        column,
    }: CellContext<ViewTableFeatures, BillsByCounterpartyItem, number>) => {
        const amount = getValue();
        const item = row.original;
        const { callbacks } = useViewTableMeta<
            BillsByVendorViewCallbacks,
            unknown
        >(table);
        const onDrilldown = callbacks?.onDrilldown;

        const columnType = column.id as BillsByVendorColumn;

        const shouldFormatCurrency =
            columnType !== BillsByVendorColumn.BILLS_COUNT;

        if (amount.toString() === "0") {
            return (
                <ViewTableCell
                    className={classNames(
                        styles.drilldownAmountCell,
                        styles[columnType],
                    )}
                >
                    <EditableCellContent readonly className={styles.content}>
                        <span className={styles.zeroAmount}>-</span>
                    </EditableCellContent>
                </ViewTableCell>
            );
        }

        const handleClick = () => {
            if (!onDrilldown) {
                return;
            }
            const params = getDrilldownParams(item, columnType);
            onDrilldown(params);
        };

        return (
            <ViewTableCell
                captureClicks
                className={classNames(
                    styles.drilldownAmountCell,
                    styles[columnType],
                )}
            >
                <EditableCellContent
                    onClick={handleClick}
                    readonly={!onDrilldown}
                    className={styles.content}
                    size="sm"
                >
                    {shouldFormatCurrency ? (
                        <Amount amount={amount} />
                    ) : (
                        <span>{amount}</span>
                    )}
                </EditableCellContent>
            </ViewTableCell>
        );
    },
);
